/**
 * Field staff onboarding attribution — Stage 0.5.
 *
 *   "I want a way to track all the profiles that THEY assisted to create
 *    profiles and login in order to properly reward and compensate them."
 *
 * TWO PATHS, and the shape of every call here is decided by something in
 * script 123 or 125 rather than by convenience.
 *
 *   GUIDED   the person signs up on their own phone while the agent talks them
 *            through it, and the agent logs the OFX Code within 12 hours.
 *   ASSISTED the agent registers them from the tablet and Orchid emails a
 *            one-time link to set a password. The claim redeems itself when an
 *            Orchid identity is attached to that address -- nothing here
 *            declares it, and nothing here can.
 *
 * LOGGING A CODE IS A BLIND WRITE. fn_log_my_field_onboarding returns
 * { recorded, message } and nothing about the person — no name, no email, no
 * room. If it echoed a name back as confirmation, Orchid would have built a
 * member lookup by OFX Code available to every field agent, which is the shape
 * prohibition 6 exists to prevent. So there is nothing here to unpack beyond
 * the verdict and the sentence, and the screen shows that sentence verbatim.
 *
 * A REFUSAL ARRIVES AS A SUCCESSFUL CALL. `recorded: false` with a message is
 * the normal answer for a code outside the twelve-hour window, one already
 * claimed, or an agent's own identity. A caller that read only `error` would
 * show nothing at all.
 */

import { supabase, isSupabaseConfigured } from './supabaseClient';

const NOT_CONFIGURED =
  'This portal was built without its Supabase credentials, so it cannot reach the database.';

const fail = (error) => ({ data: null, error });
const one = (data) => (Array.isArray(data) ? data[0] || null : data || null);

function readable(error) {
  if (!error) return null;
  // A database that has not had script 123 run. Said plainly rather than as
  // "could not find the function", which reads to somebody at a launch stand
  // as though they typed something wrong.
  if (error.code === 'PGRST202') {
    return 'Field onboarding is not switched on for this database yet.';
  }
  if (error.code === '42501') return 'Your account is not permitted to do that.';
  return error.message || 'Something went wrong.';
}

/**
 * Tidy what somebody typed on a tablet at a stand.
 *
 * Uppercased, and every space and dash stripped before the OFX- prefix is put
 * back. An agent reading a code off a phone screen types it with the spacing
 * they see, and refusing that would be refusing a correct code for a reason
 * nobody could guess.
 */
export function tidyConnectCode(raw) {
  const bare = String(raw == null ? '' : raw)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/^OFX/, '');
  return bare ? `OFX-${bare}` : '';
}

/**
 * Enough of a code to be worth sending.
 *
 * Shape only. Whether it EXISTS, and whether it was minted in the last twelve
 * hours, are the database's to answer — and it answers both with one sentence
 * on purpose, so this cannot start guessing at either.
 */
export function connectCodeProblem(raw) {
  const tidy = tidyConnectCode(raw);
  if (!tidy) return 'The OFX Code they were shown after signing up.';
  if (!/^OFX-[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{9}$/.test(tidy)) {
    return 'An OFX Code is OFX- and nine characters. It never contains 0, O, 1, I or L.';
  }
  return null;
}

/** Record that I onboarded the person holding this code. */
export async function logFieldOnboarding(code, path = 'guided') {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_log_my_field_onboarding', {
    p_ofx_connect_code: tidyConnectCode(code),
    p_path: path,
  });
  if (error) return fail(readable(error));
  return { data: one(data), error: null };
}

/** My own counts. No identities, no codes, no names — by design. */
export async function fetchMyOnboardingTally() {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_my_field_onboarding_tally');
  if (error) return fail(readable(error));
  return { data: one(data), error: null };
}

/**
 * Enough of an address to be worth sending to.
 *
 * Shape only, and deliberately loose. Whether the address EXISTS, whether it
 * already belongs to a member, and whether a colleague holds a live claim on
 * it are the database's to answer -- and it answers the first two IDENTICALLY
 * on purpose, so this must not start guessing at either.
 */
export function emailProblem(raw) {
  const tidy = String(raw == null ? '' : raw).trim().toLowerCase();
  if (!tidy) return 'The email address they gave you.';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(tidy)) {
    return 'That does not look like an email address.';
  }
  return null;
}

/**
 * Invite somebody I am sitting with. The assisted path.
 *
 * Same shape as logFieldOnboarding, for the same reason: every refusal arrives
 * as a SUCCESSFUL call carrying `invited: false` and a sentence, so a caller
 * reading only `error` would show nothing at all.
 */
export async function inviteAMemberIOnboarded(email, room = 'service') {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_invite_a_member_i_onboarded', {
    p_email: String(email == null ? '' : email).trim().toLowerCase(),
    p_room: room,
  });
  if (error) return fail(readable(error));
  return { data: one(data), error: null };
}

/**
 * The rooms a field agent may issue from a tablet.
 *
 * FOUR, IN THE ORDER THE FOUNDER SET, and this list used to be two.
 *
 *   "there should be four ways the staff member can onboard a new user: in this
 *    order - service profile (default), provider, landlord, tenant."
 *
 * The order is the instruction and it is also the advice. Service Profile is
 * first and is the default because it is the only one of the four that keeps
 * every other door open; the two that cannot be undone are last.
 *
 * WHAT THE OLD COMMENT SAID, AND WHY IT IS WORTH KEEPING
 *
 * This list was two entries, on the founder's own standing rule that "there's
 * no door back into the service profile once one migrates out" -- so a tenant
 * or a landlord chosen wrongly at a stand is a mistake nobody can take back,
 * and script 126 refused both.
 *
 * That reasoning did not stop being true. The founder has ruled the other way,
 * which does not delete the risk -- it moves it, from a rule that removed the
 * choice to the agent standing in front of the person, who can simply ask.
 * So the prohibition is gone from script 179 and the WARNING is here, in the
 * hint the agent reads while they choose.
 *
 * This list is still only a convenience for rendering. The RULING lives on the
 * CHECK constraint, because a list in a screen can grow a fifth entry in a year
 * and nothing here would refuse it.
 */
export const ROOMS = [
  {
    key: 'service',
    label: 'Service Profile',
    hint: 'Anybody who wants to find a plumber, electrician or other trade. Keeps every other door open — start here unless they have told you otherwise.',
  },
  {
    key: 'provider',
    label: 'Provider',
    hint: 'A business that does the work. They finish signing up their business themselves.',
  },
  {
    key: 'landlord',
    label: 'Landlord',
    hint: 'Somebody who rents property out. Ask before you choose it: there is no way back to a Service Profile afterwards.',
  },
  {
    key: 'tenant',
    label: 'Tenant',
    hint: 'Somebody who rents a home. Ask before you choose it: there is no way back to a Service Profile afterwards.',
  },
];

/** My own invitation counts. No addresses, ever -- by design. */
export async function fetchMyInvitations() {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_my_field_invitations');
  if (error) return fail(readable(error));
  return { data: one(data), error: null };
}

/**
 * Human Capital's ledger, one row per agent.
 *
 * REFUSAL IS NOT AN ERROR HERE. An agent who holds log_field_onboarding but
 * not view_field_onboarding_ledger is refused by the database, and that is the
 * correct and expected answer for most of the people using this screen — so it
 * comes back as `{ data: null, error: null, forbidden: true }` and the screen
 * simply does not draw that half. Showing them a red banner for working as
 * designed would be the portal telling somebody they had done something wrong.
 */
export async function fetchOnboardingLedger({ from = null, to = null } = {}) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_field_onboarding_ledger', {
    p_from: from, p_to: to,
  });
  if (error) {
    if (error.code === '42501') return { data: null, error: null, forbidden: true };
    return fail(readable(error));
  }
  return { data: data || [], error: null, forbidden: false };
}

/**
 * WHAT EACH WEEK WAS WORTH, per agent, against the terms in force that week.
 *
 * The founder, hiring the first onboarding consultant: *"we need to include
 * this points system on the Portal so that we can track and be able to
 * compensate accordingly."*
 *
 * THIS SCREEN USED TO SAY THE OPPOSITE. Its ledger paragraph read "nothing
 * here is computed into money -- that is payroll, and payroll is not this
 * platform", and that was a reasonable line to hold until the founder ruled
 * otherwise. He has. The sentence is changed rather than quietly contradicted,
 * because a screen that says one thing and does another is worse than either.
 *
 * NOTHING IS CALCULATED HERE. The points, the target and the amount all come
 * from fn_onboarding_pay_ledger, which reads each week against the
 * onboarding_terms row in force ON THAT WEEK -- so a target changed next month
 * cannot retroactively un-earn a bonus already paid. Multiplying three by a
 * provider count in JavaScript would be a second definition of the letter's
 * own arithmetic, and it would drift the first time either moved.
 *
 * REFUSED IS A NORMAL ANSWER, exactly as it is for the ledger above: an agent
 * who may log an onboarding but may not see the Human Capital view gets 42501,
 * and the screen draws nothing rather than an error.
 */
export async function fetchOnboardingPayLedger({ from = null, to = null } = {}) {
  if (!isSupabaseConfigured()) return fail(NOT_CONFIGURED);
  const { data, error } = await supabase.rpc('fn_onboarding_pay_ledger', {
    p_from: from, p_to: to,
  });
  if (error) {
    if (error.code === '42501') return { data: null, error: null, forbidden: true };
    return fail(readable(error));
  }
  return { data: data || [], error: null, forbidden: false };
}

/** Cents to rands, the way South Africa writes it. Never a float in between. */
export const randsFromCents = (cents) => {
  if (cents === null || cents === undefined) return '—';
  const n = Number(cents);
  if (!Number.isFinite(n)) return '—';
  return `R${(n / 100).toLocaleString('en-ZA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};
